import { IDBPDatabase } from 'idb'
import { BaseIndexedDB } from './BaseIndexedDB'
import { DefaultIndexedDB } from './DefaultIndexedDB'
import { SafariIndexedDB } from './SafariIndexedDB'

// Re-export the metadata shape so existing `import { FileMetadataEntry } from '@/utils/IndexedDB'`
// call sites keep working after the split.
export type { FileMetadataEntry } from './BaseIndexedDB'

/**
 * Single source of truth for picking the storage subclass.
 *
 * WebKit/Safari does not reliably persist Blobs in IndexedDB, so it needs the inline
 * ArrayBuffer format (SafariIndexedDB). Every other browser persists native Blobs, which
 * are lazy on read (cheap to enumerate) and need no special handling (DefaultIndexedDB).
 *
 * Detection is userAgent-only so it works identically in the window AND the service
 * worker (`navigator.vendor` is undefined in SW). Cached because the result is constant
 * for the lifetime of the context.
 */
let _usesInlineBlobStorageCache: boolean | null = null
export function usesInlineBlobStorage(): boolean {
  if (_usesInlineBlobStorageCache !== null) return _usesInlineBlobStorageCache
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  // Match Safari while excluding Chromium/Gecko engines and the iOS in-app variants.
  const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua)
  _usesInlineBlobStorageCache = isSafari
  return _usesInlineBlobStorageCache
}

// Choose the strategy once per context; the chosen instance is exported under the same
// name so all existing `IndexedDBSingleton.method(...)` call sites are unchanged.
const IndexedDBSingleton: BaseIndexedDB = usesInlineBlobStorage()
  ? new SafariIndexedDB()
  : new DefaultIndexedDB()

// -----------------------
// Storage Monitoring Utilities
// -----------------------

export interface StorageStats {
  totalKeys: number;
  estimatedSizeKB: number;
  keysByPrefix: Record<string, number>;
}

/**
 * Get statistics about IndexedDB storage usage.
 * Useful for debugging memory bloat issues.
 */
export async function getStorageStats(): Promise<StorageStats> {
  const keys = await IndexedDBSingleton.getAllKeys();
  const keysByPrefix: Record<string, number> = {};
  
  for (const key of keys) {
    const parts = key.split('/').filter(Boolean);
    const prefix = '/' + (parts.slice(0, 2).join('/') || 'root');
    keysByPrefix[prefix] = (keysByPrefix[prefix] || 0) + 1;
  }
  
  return {
    totalKeys: keys.length,
    estimatedSizeKB: keys.length * 50, // rough estimate
    keysByPrefix,
  };
}

/**
 * Log storage statistics to console for debugging.
 */
export async function logStorageStats(): Promise<void> {
  try {
    const stats = await getStorageStats();
    console.info('[IndexedDB Stats]', {
      totalKeys: stats.totalKeys,
      estimatedSizeMB: (stats.estimatedSizeKB / 1024).toFixed(2),
      breakdown: stats.keysByPrefix,
    });
  } catch (err) {
    console.warn('[IndexedDB Stats] Failed to get stats:', err);
  }
}

export default IndexedDBSingleton

// -----------------------
// HMR cleanup (Webpack only)
// -----------------------
// Minimal type so TS recognizes module.hot
declare const module: { hot?: { dispose(cb: () => void): void } }

if (typeof module !== 'undefined' && module?.hot) {
  module.hot.dispose(() => {
    try {
      const inst = IndexedDBSingleton as any
      const p = inst.dbPromise as Promise<IDBPDatabase> | null
      inst.dbPromise = null
      p?.then((db: IDBPDatabase) => { try { db.close() } catch {} })
    } catch {}
  })
}
